import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { c } from "react/compiler-runtime";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { GaugeValueArc as GaugeValueArc$1, GaugeReferenceArc, GaugeContainer } from "@mui/x-charts/Gauge";
import { formatValue } from "../utils/data.js";
const GaugeWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "height"
})(({
  theme,
  height = 120
}) => ({
  display: "flex",
  flexDirection: "column",
  padding: theme.sizes.spacing["01"],
  margin: 0,
  alignItems: "center",
  height,
  position: "relative",
  width: "calc(100% - 2rem)"
  // 1rem padding on each side
  // padding: theme.sizes.spacing['07'],
}));
const GaugeValueArc = styled(GaugeValueArc$1)(({
  theme
}) => ({
  fill: "url(#red-gradient)",
  strokeWidth: 0,
  stroke: "none",
  "&.indicator": {
    fill: "none",
    stroke: theme.colors.surface.primary,
    strokeWidth: 6
  },
  ".color-scheme-sand &": {
    fill: "url(#sand-gauge-gradient)"
  },
  ".color-scheme-forest &": {
    fill: "url(#forest-gauge-gradient)"
  },
  ".color-scheme-blaze &": {
    fill: "url(#blaze-gauge-gradient)"
  },
  ".color-scheme-volt &": {
    fill: "url(#volt-gauge-gradient)"
  }
}));
const ValueContainer = styled(Box)(({
  theme
}) => ({
  position: "absolute",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  bottom: 0,
  gap: theme.sizes.spacing["03"],
  "& .MuiTypography-body3": {
    color: theme.colors.text.tertiary
  },
  "& .MuiTypography-number-xl": {
    ...theme.typography["number-xl"],
    fontSize: theme.fonts.size["2xl"],
    fill: theme.colors.text.primary,
    color: theme.colors.text.primary
  }
}));
const EmptyValueContainer = styled(ValueContainer)(({
  theme
}) => ({
  // bottom: `${theme.sizes.spacing['12']}`, //1rem less than height aprox
  gap: theme.sizes.spacing.none,
  "& .MuiTypography-body-sm": {
    ...theme.typography["body-sm"],
    color: theme.colors.text.tertiary
  }
}));
const isValidCardData = (value, safeValue, isPercentage) => {
  if (isPercentage && (value < 0 || value > 1)) {
    return false;
  }
  return !(isNaN(value) || isNaN(safeValue) || safeValue < 0 || safeValue > 100);
};
const getDescriptionText = (description) => {
  if (!description) {
    return null;
  }
  if (typeof description.value === "string") {
    return description.value;
  }
  if (typeof description.value === "number") {
    return `${description.value}`;
  }
  return null;
};
const EmptyState = (t0) => {
  const $ = c(9);
  const {
    isLoading,
    height
  } = t0;
  const t1 = height || 120;
  let t2;
  if ($[0] === /* @__PURE__ */ Symbol.for("react.memo_cache_sentinel")) {
    t2 = /* @__PURE__ */ jsx(GaugeReferenceArc, {});
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== t1) {
    t3 = /* @__PURE__ */ jsx(GaugeContainer, { value: 0, valueMax: 100, valueMin: 0, innerRadius: "71%", outerRadius: "100%", height: t1, startAngle: -90, endAngle: 90, margin: 0, children: t2 });
    $[1] = t1;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== isLoading) {
    t4 = /* @__PURE__ */ jsx(EmptyValueContainer, { children: isLoading ? /* @__PURE__ */ jsx(Skeleton, { variant: "text", width: 100, height: 40 }) : /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Typography, { variant: "body-sm", children: "No data to show." }),
      /* @__PURE__ */ jsx(Typography, { variant: "body-sm", children: "Please check connections." })
    ] }) });
    $[3] = isLoading;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== height || $[6] !== t3 || $[7] !== t4) {
    t5 = /* @__PURE__ */ jsxs(GaugeWrapper, { height, children: [
      t3,
      t4
    ] });
    $[5] = height;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
const GaugeComponent = (t0) => {
  const $ = c(41);
  const {
    data,
    height,
    isLoading: t1,
    description
  } = t0;
  const isLoading = t1 === void 0 ? false : t1;
  if (!data || isLoading || data.value === void 0 || data.value === null || isNaN(data.value)) {
    let t22;
    if ($[0] !== isLoading) {
      t22 = /* @__PURE__ */ jsx(EmptyState, { isLoading });
      $[0] = isLoading;
      $[1] = t22;
    } else {
      t22 = $[1];
    }
    return t22;
  }
  const {
    value,
    goal,
    currency: t2,
    type,
    short: t3
  } = data;
  const currency = t2 === void 0 ? "SEK" : t2;
  const short = t3 === void 0 ? true : t3;
  const isPercentage = type === "percentage";
  const effectiveGoal = isPercentage ? 1 : goal || 1;
  const percentage = value / effectiveGoal * 100;
  const safeValue = percentage < 0 ? 0 : percentage > 100 ? 100 : percentage;
  let t4;
  if ($[2] !== percentage) {
    t4 = percentage.toFixed(2);
    $[2] = percentage;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const fixedPercentage = t4;
  const t5 = type;
  let t6;
  if ($[4] !== currency || $[5] !== short || $[6] !== t5 || $[7] !== value) {
    t6 = formatValue(value, {
      type: t5,
      currency,
      short
    });
    $[4] = currency;
    $[5] = short;
    $[6] = t5;
    $[7] = value;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const formattedValue = t6;
  let t7;
  if ($[9] !== description) {
    t7 = getDescriptionText(description);
    $[9] = description;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const descriptionText = t7;
  const hasExplicitGoal = !!goal && goal > 0;
  const shouldShowGaugeValueArcs = isPercentage || hasExplicitGoal;
  const shouldShowGoal = !isPercentage && hasExplicitGoal;
  if (!isValidCardData(value, safeValue, isPercentage)) {
    let t82;
    if ($[11] !== height) {
      t82 = /* @__PURE__ */ jsx(EmptyState, { isLoading: false, height });
      $[11] = height;
      $[12] = t82;
    } else {
      t82 = $[12];
    }
    return t82;
  }
  const t8 = height || 120;
  let t9;
  if ($[13] === /* @__PURE__ */ Symbol.for("react.memo_cache_sentinel")) {
    t9 = /* @__PURE__ */ jsx(GaugeReferenceArc, {});
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== shouldShowGaugeValueArcs) {
    t10 = shouldShowGaugeValueArcs && /* @__PURE__ */ jsx(GaugeValueArc, { className: "indicator" });
    $[14] = shouldShowGaugeValueArcs;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== shouldShowGaugeValueArcs) {
    t11 = shouldShowGaugeValueArcs && /* @__PURE__ */ jsx(GaugeValueArc, {});
    $[16] = shouldShowGaugeValueArcs;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== safeValue || $[19] !== t10 || $[20] !== t11 || $[21] !== t8) {
    t12 = /* @__PURE__ */ jsxs(GaugeContainer, { value: safeValue, valueMax: 100, valueMin: 0, height: t8, innerRadius: "71%", outerRadius: "100%", startAngle: -90, margin: 0, endAngle: 90, children: [
      t9,
      t10,
      t11
    ] });
    $[18] = safeValue;
    $[19] = t10;
    $[20] = t11;
    $[21] = t8;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== formattedValue) {
    t13 = /* @__PURE__ */ jsx(Typography, { variant: "number-xl", children: formattedValue });
    $[23] = formattedValue;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== descriptionText) {
    t14 = descriptionText && /* @__PURE__ */ jsx(Typography, { variant: "body3", children: descriptionText });
    $[25] = descriptionText;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== fixedPercentage || $[28] !== goal || $[29] !== short || $[30] !== shouldShowGoal || $[31] !== type) {
    t15 = shouldShowGoal && /* @__PURE__ */ jsx(Typography, { variant: "body3", children: `${fixedPercentage}% of ${formatValue(goal, {
      short,
      precision: 0,
      type
    })}` });
    $[27] = fixedPercentage;
    $[28] = goal;
    $[29] = short;
    $[30] = shouldShowGoal;
    $[31] = type;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t13 || $[34] !== t14 || $[35] !== t15) {
    t16 = /* @__PURE__ */ jsxs(ValueContainer, { children: [
      t13,
      t14,
      t15
    ] });
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== height || $[38] !== t12 || $[39] !== t16) {
    t17 = /* @__PURE__ */ jsxs(GaugeWrapper, { height, children: [
      t12,
      t16
    ] });
    $[37] = height;
    $[38] = t12;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  return t17;
};
export {
  GaugeComponent as default
};
